/**
 * Image Generation Progress Component
 *
 * Shows the current progress of image generation with animated stages.
 * During the 'generating' stage, smoothly animates progress over an estimated
 * duration to provide better UX feedback.
 */

import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ImageGenerationStage =
  | 'idle'
  | 'starting'
  | 'generating'
  | 'downloading'
  | 'storing'
  | 'complete'
  | 'error';

interface ImageGenerationProgressProps {
  stage: ImageGenerationStage;
  message: string;
  className?: string;
  /**
   * Estimated generation duration in milliseconds.
   * Defaults to 18000ms (18 seconds), suitable for most fast models.
   * Use 25000ms for slower models like Imagen 3.
   */
  estimatedDurationMs?: number;
}

// Static progress values for non-generating stages
// These should be monotonically increasing to prevent backward jumps
const STAGE_PROGRESS: Record<ImageGenerationStage, number> = {
  idle: 0,
  starting: 10,
  generating: 50, // Not used during animation, but kept as fallback
  downloading: 92, // Higher than GENERATING_MAX_PROGRESS (90) to prevent backward jump
  storing: 96,
  complete: 100,
  error: 0,
};

// Time-based animation config for 'generating' stage
const DEFAULT_GENERATING_DURATION_MS = 18000; // 18 seconds
const GENERATING_START_PROGRESS = 10;
const GENERATING_MAX_PROGRESS = 90; // Cap at 90% to make progress feel faster

// Easing function: easeOutCubic - starts fast, slows down naturally
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function ImageGenerationProgress({
  stage,
  message,
  className,
  estimatedDurationMs = DEFAULT_GENERATING_DURATION_MS,
}: ImageGenerationProgressProps) {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const generatingStartTimeRef = useRef<number | null>(null);
  const randomBoostRef = useRef<number>(0);
  const lastBoostUpdateRef = useRef<number>(0);
  const highestProgressRef = useRef<number>(0);
  const estimatedDurationRef = useRef(estimatedDurationMs);
  estimatedDurationRef.current = estimatedDurationMs;

  // Handle progress animation
  useEffect(() => {
    if (stage === 'generating') {
      // Record when generating started (only on first render of this stage)
      if (generatingStartTimeRef.current === null) {
        generatingStartTimeRef.current = Date.now();
        randomBoostRef.current = 0;
        lastBoostUpdateRef.current = Date.now();
        highestProgressRef.current = GENERATING_START_PROGRESS;
        // Set initial progress immediately
        setAnimatedProgress(GENERATING_START_PROGRESS);
      }

      const interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - generatingStartTimeRef.current!;
        const progressFraction = Math.min(elapsed / estimatedDurationRef.current, 1);

        // Apply easing for natural slowdown as it approaches max
        const eased = easeOutCubic(progressFraction);
        const progressRange = GENERATING_MAX_PROGRESS - GENERATING_START_PROGRESS;
        const baseProgress = GENERATING_START_PROGRESS + progressRange * eased;

        // Update random boost every 500-1500ms for natural feel
        if (now - lastBoostUpdateRef.current > 500 + Math.random() * 1000) {
          // Random boost between 0 and +3 percent (only forward, never backward)
          randomBoostRef.current = Math.random() * 3;
          lastBoostUpdateRef.current = now;
        }

        // Apply boost but keep within bounds
        const boostedProgress = Math.min(
          GENERATING_MAX_PROGRESS,
          baseProgress + randomBoostRef.current,
        );

        // Never go backwards - always use the highest value seen
        const newProgress = Math.max(highestProgressRef.current, boostedProgress);
        highestProgressRef.current = newProgress;

        setAnimatedProgress(Math.round(newProgress));
      }, 100); // Update every 100ms for smooth animation

      return () => clearInterval(interval);
    } else {
      // Reset generating-specific state when leaving generating stage
      generatingStartTimeRef.current = null;
      randomBoostRef.current = 0;

      // Reset everything when going to idle or complete (preparing for next generation)
      if (stage === 'idle' || stage === 'complete' || stage === 'error') {
        highestProgressRef.current = 0;
        setAnimatedProgress(STAGE_PROGRESS[stage]);
      } else {
        // Use static progress for other stages, but never go backwards
        const stageProgress = STAGE_PROGRESS[stage];
        const newProgress = Math.max(highestProgressRef.current, stageProgress);
        highestProgressRef.current = newProgress;
        setAnimatedProgress(newProgress);
      }
    }
  }, [stage]);

  if (stage === 'idle' || stage === 'complete') {
    return null;
  }

  const progress = animatedProgress;
  const isError = stage === 'error';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-8 rounded-xl border bg-card',
        isError ? 'border-destructive' : 'border-border',
        className,
      )}
    >
      {/* Spinner or error icon */}
      {!isError && (
        <div className="relative mb-6">
          <div className="w-20 h-20 rounded-full border-4 border-muted flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        </div>
      )}

      {/* Progress message */}
      <p
        className={cn(
          'text-lg font-medium text-center',
          isError ? 'text-destructive' : 'text-foreground',
        )}
      >
        {message}
      </p>

      {/* Progress bar */}
      {!isError && (
        <div className="w-full max-w-xs mt-6">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-150 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-muted-foreground text-center mt-2">{progress}%</p>
        </div>
      )}
    </div>
  );
}

export default ImageGenerationProgress;
